import { Box, Grid, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { CancelButton } from '../components/CancelButton'
import {
  AppForm,
  AppFormProps,
  FormCheckbox,
  FormFileUpload,
  FormSubmitButton,
  FormTextField,
} from '../components/FormCore'
import { FileType } from '../enums'
import { ICustomer } from '../generated-types'
import { useZodForm } from '../hooks/zod-form'
import { useGetUniqueAdminCustomers, useGetCustomerFromGovAPI } from 'src/api/customer'
import { useGetFile } from 'src/api/file'
import { CustomerCompanyType } from 'src/enums'
import { FormSelectField } from 'src/components/FormCore'
import { AppTextField } from 'src/components/AppTextField'
import { Controller } from 'react-hook-form'

const schema = z.object({
  name: z.string().nonempty(),
  image: z.string().optional().nullable(),
  organizationNr: z.string().nonempty(),
  address: z.string().nonempty(),
  zipCode: z.string().nonempty(),
  city: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
  externalCustomerNumber: z.string().optional(),
})

export type CustomerFormType = z.infer<typeof schema>

export const CustomerForm: React.FC<AppFormProps<ICustomer>> = ({ onSubmit, onCancel, initialValues }) => {
  const { t: translate } = useTranslation(['admin-customer', 'common', 'custom-property'])
  const { data } = useGetUniqueAdminCustomers()
  const isUpdateForm = initialValues?.id
  const { data: file } = useGetFile(initialValues?.fileId ?? 0)

  const [orgNo, setOrgNo] = useState<string | undefined>(undefined)
  const { data: orgData } = useGetCustomerFromGovAPI(orgNo)

  useEffect(() => {
    if (initialValues) {
      form.setValue('externalCustomerNumber', initialValues.externalCustomerNumber ?? '')
    }
  }, [initialValues])

  useEffect(() => {
    if (orgData) {
      form.setValue('name', orgData.Name ?? '')
      form.setValue('address', orgData.Address ?? '')
      form.setValue('zipCode', orgData.PostalCode ?? '')
      form.setValue('city', orgData.City ?? '')
      form.setValue('country', orgData.Country ?? '')
      form.setValue('phone', orgData.Phone ?? '')
 
    }
  }, [orgData])

  const handleInputBlur = async event => {
    const inputValue = event.target.value?.trim()
    setOrgNo(inputValue || undefined)
  }

  const nameValidation = v => {
    if (isUpdateForm && initialValues?.name == v) {
      return true
    }
    return !data?.find(u => u.name == v)
  }
  type CustomerTypeOption = {
    id: string
    label: string
  }

  const organizationNumberValidation = v => {
    if (isUpdateForm && initialValues?.organizationNr == v) {
      return true
    }
    return !data?.find(u => u.organizationNr == v)
  }

  const customerAddScema = z.object({
    name: z
      .string()
      .refine(nameValidation, { message: translate('duplicateName', { ns: 'custom-property' }) })
      .optional(),
    image: z.string().optional().nullable(),
    fileId: z.union([z.string(), z.number()]).or(z.null()).optional(),
    organizationNr: z
      .string()
      .refine(organizationNumberValidation, {
        message: translate('duplicateOrganizationNumber', { ns: 'custom-property' }),
      })
      .optional(),
    address: z.string().nonempty(),
    zipCode: z.string().nonempty(),
    city: z.string().optional(),
    country: z.string().optional(),
    phone: z.string().optional(),
    externalCustomerNumber: z.string().optional(),
    isGlobalServiceStationProvider: z.boolean().optional(),
  })

  const form = useZodForm(customerAddScema, { defaultValues: initialValues })

  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())
  }

  return (
    <AppForm form={form} onSubmit={onSubmit} hasInitialValues={!!initialValues}>
      {/* <FormTextField fullWidth name="name" label={translate('name', { ns: 'common' })} /> */}
      <Grid container>
        <Grid item xs={12} sm={12}>
          <FormTextField
            fullWidth
            name="organizationNr"
            label={translate('organizationNumber')}
            onBlur={handleInputBlur}
          />
          <Typography variant="caption" color="textSecondary" sx={{ margin: 1, display: 'block' }}>
            {translate('org-number-auto-fetch-text')}
          </Typography>

          <FormSelectField
            name="name"
            label={translate('name', { ns: 'common' })}
            freeSolo
            data={[] as CustomerTypeOption[]}
            getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
            onChange={(_, value, reason, details) => {
              let finalValue = ''

              if (typeof value === 'string') {
                finalValue = value
              } else if (Array.isArray(value)) {
                const first = value[0]
                finalValue = typeof first === 'string' ? first : first?.label ?? ''
              } else if (value) {
                finalValue = value.label
              }
              form.setValue('name', finalValue)
              return finalValue
            }}
            filterOptions={(_, params) => {
              if (
                !(
                  params.inputValue.includes(`${CustomerCompanyType.AS}`) ||
                  params.inputValue.includes(`${CustomerCompanyType.ASA}`)
                )
              ) {
                const inputValue = params.inputValue?.trim()

                if (!inputValue) {
                  return []
                }

                return [
                  {
                    id: `01-${inputValue}`,
                    label: `${toTitleCase(inputValue)} ${CustomerCompanyType.AS}`,
                  },
                  {
                    id: `02-${inputValue}`,
                    label: `${toTitleCase(inputValue)} ${CustomerCompanyType.ASA}`,
                  },
                ]
              } else {
                form.setValue('name', params.inputValue)
                return []
              }
            }}
          />
        </Grid>
      </Grid>

      <Box py={1}>
        <FormFileUpload
          label={translate('image', { ns: 'common' })}
          name="fileId"
          mapFileKey="id"
          type={FileType.Image}
          initialFiles={file}
        />
      </Box>
      <Controller
        name="address"
        control={form.control}
        render={({ field, fieldState }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            label={translate('address', { ns: 'common' })}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="zipCode"
        control={form.control}
        render={({ field, fieldState }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            label={translate('zipCode', { ns: 'common' })}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="city"
        control={form.control}
        render={({ field, fieldState }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            label={translate('city', { ns: 'common' })}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="country"
        control={form.control}
        render={({ field, fieldState }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            label={translate('country', { ns: 'common' })}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="phone"
        control={form.control}
        render={({ field, fieldState }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            label={translate('phone', { ns: 'common' })}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        name="externalCustomerNumber"
        control={form.control}
        render={({ field, fieldState }) => (
          <AppTextField
            {...field}
            value={field.value ?? ''}
            fullWidth
            label={translate('PengVin-Customer-Number', { ns: 'common' })}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />

      <FormCheckbox name="isGlobalServiceStationProvider" label={translate('isGSSProvider', { ns: 'common' })} />
      <Box className="flex justify-end gap-4 pt-4">
        <FormSubmitButton
          createText={isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        />
        <CancelButton onClick={onCancel}>{translate('cancel', { ns: 'common' })}</CancelButton>
      </Box>
    </AppForm>
  )
}
